import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CalendarSyncPage,
  StaysSyncError,
  SyncFeed,
  SyncReview,
  getCalendarSync,
  listSyncReviews,
  removeFeed,
  syncFeedNow,
} from '../../../../api/stays-sync-client';
import type { StaysListingHead } from '../../../../interfaces/stays';
import { AppButton } from '../../../button/app-button';
import { ClockNote } from '../../../clock-note/clock-note';
import { HelperText } from '../../../form-kit';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { useNotificationFrames } from '../../../notifications/use-notification-frames';
import { Staleable, StateSwitch } from '../../../../motion';
import { ListSkeleton, LoadError, skeletonHeight } from '../../dashboard/dashboard-shared-components/load-state';
import { resolveClock } from '../../../../function-utils/building-time';
import { countOf } from '../../../../function-utils/plural';
import toast from '../../../../utils/toast';
import { feedTitle, providerName, reasonWords, retryWords } from '../../../../utils/stays-sync';
import { ConnectFeedModal } from './connect-feed-modal';
import { ExportCard } from './export-card';
import { FeedRow } from './feed-row';
import { SyncBanners } from './sync-banners';
import { SyncReviewList } from './sync-review-list';
import { Card, H3, Muted, Page, Rows } from './sync.styled';

/**
 * A LISTING'S "CALENDAR SYNC" TAB (plan S5, screens §1; appended to S4's
 * STAYS_LISTING_TABS):
 *   0. the banners (failing > review > guest name > not connected; two at most);
 *   1. the changes waiting for the host: the first 5, then "See all n changes"
 *      (the full list is /dashboard/stays/sync-review?listing=<id>);
 *   2. "Import from booking sites": one row per connected calendar, Connect;
 *   3. "Export to booking sites": one link per destination.
 * stays:read sees it all; every control that changes anything needs
 * stays:manage and is not rendered without it. Times are on the reader's
 * clock (R-T12) with a ClockNote; the page refreshes when a "calendar changes
 * to review" notification arrives and every 60 s while visible.
 */

const REFRESH_MS = 60_000;

export const CalendarSyncTab: React.FC<{ listing: StaysListingHead; canManage: boolean }> = ({ listing, canManage }) => {
  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [data, setData] = useState<CalendarSyncPage | null>(null);
  const [reviews, setReviews] = useState<SyncReview[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [modal, setModal] = useState<{ mode: 'create' | 'settings'; feed: SyncFeed | null } | null>(null);
  const [removing, setRemoving] = useState<SyncFeed | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [waits, setWaits] = useState<Record<string, string>>({});
  const seq = useRef(0);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++seq.current;
      if (quiet) setRefreshing(true);
      else setView('loading');
      try {
        // The review list is a second read; its failure leaves the page (and the banner's count) standing.
        const [out, list] = await Promise.all([
          getCalendarSync(listing.id),
          listSyncReviews({ listing_id: listing.id, state: 'pending' }).catch(() => null),
        ]);
        if (mine !== seq.current) return;
        setData(out);
        setReviews(list ? list.items : []);
        setNow(new Date());
        setView('ready');
      } catch {
        if (mine !== seq.current) return;
        if (!quiet) setView('error');
      } finally {
        if (mine === seq.current) setRefreshing(false);
      }
    },
    [listing.id]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(true);
    }, REFRESH_MS);

    return () => window.clearInterval(id);
  }, [load]);

  useEffect(
    () => () => {
      for (const t of Object.values(timers.current)) clearTimeout(t);
    },
    []
  );

  useNotificationFrames(row => {
    if (row.event_type === 'stays.sync_review_needed') void load(true);
  });

  const clock = resolveClock({ surface: 'app', response: data, buildingZone: listing.timezone });
  const can = canManage && (!data || data.can_manage);

  const waitFor = (feedId: string, seconds: number) => {
    setWaits(w => ({ ...w, [feedId]: retryWords(seconds) }));
    if (timers.current[feedId]) clearTimeout(timers.current[feedId]);
    timers.current[feedId] = setTimeout(() => {
      setWaits(w => {
        const next = { ...w };
        delete next[feedId];

        return next;
      });
    }, Math.max(1, seconds) * 1000);
  };

  const syncNow = async (feed: SyncFeed) => {
    if (syncing) return;
    setSyncing(feed.id);
    const title = feedTitle(feed);
    try {
      const out = await syncFeedNow(feed.id);
      if (out.result === 'ok' || out.result === 'not_modified') {
        const n = out.changes.reviews_created;
        toast.success(`${title} checked · ${n ? `${countOf(n, 'change')} to review` : 'nothing new'}`);
      } else {
        toast.error(`The ${title} could not be checked: ${reasonWords(out.result)}.`);
      }
      waitFor(feed.id, 60);
      await load(true);
    } catch (err) {
      const x = err as StaysSyncError;
      if (x && x.code === 'SYNC_TOO_SOON') waitFor(feed.id, x.retryAfter || 60);
      else toast.error(x && x.status > 0 && x.message ? x.message : `The ${title} could not be checked. Please try again.`);
    } finally {
      setSyncing(null);
    }
  };

  const remove = async () => {
    const feed = removing;
    setRemoving(null);
    if (!feed) return;
    try {
      await removeFeed(feed.id);
      toast.success(`${feedTitle(feed)} removed`);
      await load(true);
    } catch (err) {
      const x = err as StaysSyncError;
      toast.error(x && x.status > 0 && x.message ? x.message : 'The calendar could not be removed. Please try again.');
    }
  };

  const feeds = data ? data.feeds : [];
  const taken = feeds.filter(f => f.provider !== 'other').map(f => f.provider);
  const pending = data ? data.reviews.pending : 0;
  const removeName = removing ? providerName(removing.provider) : '';

  return (
    <Page data-testid="calendar-sync-tab">
      <StateSwitch state={view} minHeight={skeletonHeight(3, 96)}>
        {view === 'loading' ? <ListSkeleton rows={3} rowHeight={96} label="calendar sync" /> : null}
        {view === 'error' ? <LoadError message="Couldn't load the calendar sync" onRetry={() => void load(false)} /> : null}
        {view === 'ready' && data ? (
          <Staleable stale={refreshing}>
            <Page>
              <SyncBanners
                scope="listing"
                listingId={listing.id}
                listingName={listing.name}
                page={data}
                zone={clock.zone}
                now={now}
                canManage={can}
              />
              {pending > 0 ? (
                <Card aria-labelledby="sync-review-title" data-testid="sync-review-summary">
                  <H3 id="sync-review-title">Changes to review</H3>
                  <SyncReviewList
                    items={reviews}
                    canManage={can}
                    zone={clock.zone}
                    now={now}
                    limit={5}
                    total={pending}
                    seeAllHref={`/dashboard/stays/sync-review?listing=${encodeURIComponent(listing.id)}`}
                    onChanged={() => void load(true)}
                  />
                </Card>
              ) : null}

              <Card aria-labelledby="sync-import-title" data-testid="sync-import-card">
                <H3 id="sync-import-title">Import from booking sites</H3>
                <HelperText>
                  {`Lobbero checks each calendar about every ${data.cadence_minutes} minutes. Booking sites publish changes with their own delay, often a few hours.`}
                </HelperText>
                <ClockNote clock={clock} />
                {feeds.length ? (
                  <Rows>
                    {feeds.map(f => (
                      <FeedRow
                        key={f.id}
                        feed={f}
                        zone={clock.zone}
                        now={now}
                        canManage={can}
                        syncing={syncing === f.id}
                        waitReason={waits[f.id] || null}
                        onSync={feed => void syncNow(feed)}
                        onSettings={feed => setModal({ mode: 'settings', feed })}
                        onRemove={feed => setRemoving(feed)}
                      />
                    ))}
                  </Rows>
                ) : (
                  <Muted role="status">
                    No calendar connected yet. Connect Airbnb, Booking.com or Vrbo so their stays arrive here.
                  </Muted>
                )}
                {data.guest_name_required > 0 ? (
                  <Muted>{`${countOf(data.guest_name_required, 'imported stay')} still ${data.guest_name_required === 1 ? 'needs' : 'need'} the guest's name.`}</Muted>
                ) : null}
                {can ? (
                  <div>
                    <AppButton type="button" onClick={() => setModal({ mode: 'create', feed: null })}>
                      Connect a calendar
                    </AppButton>
                  </div>
                ) : null}
              </Card>

              <ExportCard listingId={listing.id} exports={data.exports} canManage={can} now={now} onChanged={() => void load(true)} />
            </Page>
          </Staleable>
        ) : null}
      </StateSwitch>

      {can ? (
        <ConnectFeedModal
          open={!!modal}
          mode={modal ? modal.mode : 'create'}
          listingId={listing.id}
          feed={modal ? modal.feed : null}
          taken={taken}
          onClose={() => setModal(null)}
          onConnected={() => {
            setModal(null);
            void load(true);
          }}
          onSaved={() => {
            setModal(null);
            void load(true);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={!!removing}
        title={`Remove the ${removeName} calendar?`}
        message={`New stays, date changes and cancellations from ${removeName} will stop arriving. Stays already imported stay on the calendar, and ${removeName}'s blocked dates are removed.`}
        primaryLabel={`Remove the ${removeName} calendar`}
        primaryVariant="destructive"
        onPrimary={remove}
        dismissLabel="Keep it"
        onDismiss={() => setRemoving(null)}
      />
    </Page>
  );
};
